import { getUTMParameters } from './utm';
import { isBot } from './bot-detection';
import { parseReferrer } from './referrer-parser';

// Public PostHog project API key (shared with Flash/Nightwing — single project).
const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY || '';
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';

// Browser-noise exceptions that carry no actionable signal — dropped before they
// ever reach PostHog (keeps error tracking clean + saves ingestion quota):
//   • ResizeObserver loop … — benign layout-reflow warning, never a real bug
//   • Script error. — opaque cross-origin error with no stack/message
//   • Non-Error promise rejection captured — a rejected promise with a non-Error value
const EXCEPTION_NOISE = [
  'ResizeObserver loop',
  'Script error.',
  'Non-Error promise rejection captured',
  // Client-side network/connectivity failures (user offline, request aborted,
  // tab closed mid-flight). Not actionable from the frontend — real API outages
  // are caught server-side and surfaced in Slack #incidents.
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource',
  // Third-party scripts we don't control:
  'Could not load "', // Google Maps JS API loader (maps/api/js)
  'Java object is gone', // Android WebView keyboard bridge
  'window.webkit.messageHandlers', // iOS in-app WebView (WKWebView) bridge probe — undefined in mobile Safari
  '/maps/api/js', // runtime errors thrown from inside Google Maps' own minified code
];

function isNoiseException(properties) {
  if (!properties) return false;
  const messages = [];
  const list = properties.$exception_list;
  if (Array.isArray(list)) {
    for (const ex of list) {
      if (ex?.value) messages.push(String(ex.value));
      if (ex?.type) messages.push(String(ex.type));
      // Stack-frame sources let us drop errors thrown from inside third-party
      // origins (e.g. Google Maps) regardless of their minified message.
      const frames = ex?.stacktrace?.frames;
      if (Array.isArray(frames)) {
        for (const f of frames) {
          if (f?.source) messages.push(String(f.source));
        }
      }
    }
  }
  if (properties.$exception_message) messages.push(String(properties.$exception_message));
  const haystack = messages.join(' ');
  return EXCEPTION_NOISE.some((p) => haystack.includes(p));
}

let posthog = null;
let isReady = false;
let initPromise = null;
let detectedBot = false;
// Atribución del visitante anónimo. Se registra como super properties al init y se
// vuelca al perfil recién cuando la persona se identifica (ver initAnalytics).
let attributionProps = {};
let attributionPropsOnce = {};
const eventQueue = [];

function isBrowser() {
  return typeof window !== 'undefined';
}

function enqueue(action) {
  if (isReady) {
    action();
  } else {
    eventQueue.push(action);
  }
}

function flushQueue() {
  while (eventQueue.length > 0) {
    const action = eventQueue.shift();
    action();
  }
}

/**
 * Initialize PostHog for the marketplace (market.agora.red).
 *
 * Events-only configuration — no session recording, no autocapture, no
 * automatic pageviews (page views are tracked explicitly via usePageTracking).
 *
 * cross_subdomain_cookie shares the anonymous distinct_id across .agora.red, so
 * a visitor coming from the storefront/dashboard correlates natively.
 *
 * The SDK is dynamically imported so it stays out of the initial bundle.
 */
export function initAnalytics() {
  if (!isBrowser()) return Promise.resolve();
  if (initPromise) return initPromise;

  const isProd = process.env.NODE_ENV === 'production';

  if (!isProd || !POSTHOG_KEY) {
    if (!isProd) console.log('[PostHog] Dev mode — events logged to console');
    isReady = true;
    flushQueue();
    initPromise = Promise.resolve();
    return initPromise;
  }

  initPromise = import('posthog-js')
    .then((mod) => {
      posthog = mod.default;

      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        autocapture: false,
        capture_pageview: false,
        capture_pageleave: false,
        // EXPLÍCITOS en `false`, no omitidos: lo que no se declara acá lo decide el
        // remote config del PROYECTO. El 5/ago/2026 se prendieron los toggles de
        // proyecto a propósito para leer dónde tocan los vendors en el alta de Flash,
        // y se colaron en toda la red: nightwing lo tapó ese mismo día, el
        // marketplace no, y quedó emitiendo `$dead_click` + `$dead_swipe` desde
        // entonces (~2,5k/mes al 10/sep). Ninguna pantalla del marketplace tiene una
        // pregunta abierta de "¿dónde tocan?" que justifique ese costo.
        capture_dead_clicks: false,
        capture_heatmaps: false,
        disable_session_recording: true,
        disable_surveys: true,
        cross_subdomain_cookie: true,
        // El visitante anónimo NO genera perfil de persona. El marketplace es público
        // y 100% crawleable: desde el 20/ago/2026 una flota de headless Chrome
        // (1920x1080 y en-US en el 100% de los hits, UA rotada entre Chrome 135-149,
        // proxies residenciales repartidos por Brasil/Bangladesh/Pakistán/Vietnam/…)
        // recorre las páginas de combinación long-tail. Cada visita creaba un perfil
        // y emitía un `$set`: pasamos de ~250 a ~10.400 personas/día, ~190k eventos/mes
        // contra el free tier de 1M — con 1,05 eventos por "persona", o sea ninguna
        // sesión real detrás. El perfil se crea recién en identify (usuario logueado).
        person_profiles: 'identified_only',
        persistence: 'localStorage+cookie',
        // Drop zero-signal browser-noise exceptions at the source (before ingestion).
        before_send: (event) => {
          if (event?.event === '$exception' && isNoiseException(event.properties)) return null;
          return event;
        },
      });

      // Error tracking: auto-capture unhandled exceptions + promise rejections.
      posthog.startExceptionAutocapture();

      // Atribución del visitante: bot flag, first-touch, traffic source.
      //
      // Van como SUPER PROPERTIES (register/register_once), no como person properties.
      // `setPersonProperties` emite un evento `$set` propio por visitante — y ese
      // evento se factura, así que cada crawler que ejecuta JS costaba uno aunque no
      // hiciera absolutamente nada más (era el 80% del volumen del marketplace).
      // Como super properties viajan pegadas a los eventos que ya emitimos: mismo
      // dato, costo cero. `register_once` persiste en localStorage, así que el
      // first-touch sigue siendo el de la primera visita real. Se materializan en el
      // perfil recién en identifyUser(), dentro del `$identify` que ya se emitía.
      detectedBot = isBot();
      const referrerData = parseReferrer(document.referrer);
      const utmParams = getUTMParameters();

      attributionProps = {
        is_bot: detectedBot,
        last_traffic_source: referrerData.source,
        last_traffic_medium: referrerData.medium,
        ...utmParams,
      };
      attributionPropsOnce = {
        first_landing_page: window.location.pathname,
        first_referrer: document.referrer || 'direct',
        is_new_visitor: true,
        first_traffic_source: referrerData.source,
        first_traffic_medium: referrerData.medium,
      };
      Object.entries(utmParams).forEach(([key, value]) => {
        attributionPropsOnce[`first_${key}`] = value;
      });

      posthog.register(attributionProps);
      posthog.register_once(attributionPropsOnce);

      isReady = true;
      flushQueue();
    })
    .catch((err) => {
      console.error('[PostHog] Failed to load SDK:', err);
    });

  return initPromise;
}

export function identifyUser(userId, properties) {
  if (!isBrowser()) return;

  const isProd = process.env.NODE_ENV === 'production';
  if (!isProd || !POSTHOG_KEY) {
    console.log('[PostHog] identifyUser:', userId, properties);
    return;
  }

  enqueue(() => {
    // El identify es el momento en que nace el perfil (person_profiles:
    // 'identified_only'), así que la atribución que veníamos arrastrando como super
    // properties se vuelca acá — en el `$identify` que ya se emitía, sin un evento extra.
    posthog.identify(
      String(userId),
      { ...attributionProps, ...properties, is_authenticated: true },
      attributionPropsOnce
    );
  });
}

export function resetUser() {
  if (!isBrowser()) return;

  const isProd = process.env.NODE_ENV === 'production';
  if (!isProd || !POSTHOG_KEY) {
    console.log('[PostHog] resetUser');
    return;
  }

  enqueue(() => {
    posthog.reset();
  });
}

// Low-value, high-volume engagement noise suppressed to keep event volume down.
const SUPPRESSED_EVENTS = new Set(['engagement_scroll_depth', 'engagement_time_on_page']);

// De-duplicate rapid re-fires (same event + props within a short window) caused
// by component re-renders — these are never real usage.
const DEDUP_WINDOW_MS = 1500;
const recentEvents = new Map();
function isDuplicateEvent(key) {
  const now = Date.now();
  if (recentEvents.size > 300) {
    for (const [k, t] of recentEvents) if (now - t > DEDUP_WINDOW_MS) recentEvents.delete(k);
  }
  const last = recentEvents.get(key);
  recentEvents.set(key, now);
  return last !== undefined && now - last < DEDUP_WINDOW_MS;
}

export function trackEvent(eventName, properties) {
  if (!isBrowser()) return;
  if (SUPPRESSED_EVENTS.has(eventName)) return;
  if (isDuplicateEvent(eventName + '|' + JSON.stringify(properties || {}))) return;

  const isProd = process.env.NODE_ENV === 'production';
  const allProperties = { ...properties, ...getUTMParameters() };

  if (!isProd || !POSTHOG_KEY) {
    console.log(`[PostHog] ${eventName}`, allProperties);
    return;
  }

  enqueue(() => {
    posthog.capture(eventName, allProperties);
  });
}

// Error tracking — manual capture (for caught errors / the Next error boundaries).
// Unhandled errors are auto-captured via startExceptionAutocapture().
export function captureException(error, context) {
  if (!isBrowser()) return;
  if (process.env.NODE_ENV !== 'production' || !POSTHOG_KEY) {
    console.error('[PostHog] captureException (dev):', error);
    return;
  }
  initAnalytics();
  enqueue(() => posthog.captureException(error, context));
}
